import React from 'react';
import { PurchaseOrderAuditEvent } from '@/types/domain/po-management';
import { formatDate } from '@/lib/utils';
import { History, Shield, Database, UserCheck, AlertOctagon } from 'lucide-react';

interface PoAuditHistoryTabProps {
  auditHistory: PurchaseOrderAuditEvent[];
}

export const PoAuditHistoryTab: React.FC<PoAuditHistoryTabProps> = ({ auditHistory }) => {
  if (!auditHistory || auditHistory.length === 0) {
    return (
      <div className="p-8 text-center bg-card rounded-2xl border border-border">
        <History className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
        <p className="text-xs font-semibold text-foreground-secondary">
          No audit entries recorded for this Purchase Order yet.
        </p>
      </div>
    );
  }

  const getEventBadge = (type: PurchaseOrderAuditEvent['eventType']) => {
    switch (type) {
      case 'PO_CREATED':
        return <span className="bg-blue-500/10 text-blue-700 border border-blue-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">CREATED</span>;
      case 'PO_SUBMITTED':
        return <span className="bg-purple-500/10 text-purple-700 border border-purple-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">SUBMITTED</span>;
      case 'PO_APPROVED':
        return <span className="bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">APPROVED</span>;
      case 'PO_RELEASED':
      case 'PO_ISSUED':
        return <span className="bg-teal-500/10 text-teal-700 border border-teal-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">{type}</span>;
      case 'PO_REJECTED':
      case 'PO_CANCELLED':
        return <span className="bg-rose-500/10 text-rose-700 border border-rose-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">{type}</span>;
      case 'PO_SENT_BACK':
        return <span className="bg-amber-500/10 text-amber-700 border border-amber-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">SENT BACK</span>;
      case 'SAP_SYNC_SUCCESS':
        return <span className="bg-indigo-500/10 text-indigo-700 border border-indigo-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">SAP SYNC</span>;
      case 'PO_AMENDED':
        return <span className="bg-cyan-500/10 text-cyan-700 border border-cyan-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">AMENDED</span>;
      default:
        return <span className="bg-slate-500/10 text-slate-700 border border-slate-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">{type}</span>;
    }
  };

  const getSourceIcon = (source: PurchaseOrderAuditEvent['systemSource']) => {
    switch (source) {
      case 'SAP_BAPI':
        return <Database className="h-3.5 w-3.5 text-purple-600" />;
      case 'System':
        return <AlertOctagon className="h-3.5 w-3.5 text-slate-500" />;
      default:
        return <UserCheck className="h-3.5 w-3.5 text-primary" />;
    }
  };

  return (
    <div className="space-y-4 font-heading">
      <div className="flex items-center justify-between pb-2 border-b border-border/60">
        <div>
          <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" /> SOX & Enterprise Regulatory Audit Log
          </h3>
          <p className="text-[11px] text-foreground-secondary">
            Immutable chronological record of approvals, modifications, SAP BAPI calls, and lifecycle transitions.
          </p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted px-2.5 py-1 rounded-lg bg-surface border border-border">
          {auditHistory.length} Recorded Events
        </span>
      </div>

      <div className="divide-y divide-border/60 rounded-2xl border border-border bg-card overflow-hidden">
        {auditHistory.map((item) => (
          <div key={item.id} className="p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3 hover:bg-surface/50 transition-colors">
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center gap-2">
                {getEventBadge(item.eventType)}
                <span className="text-xs font-bold text-foreground">{item.actorName}</span>
                <span className="text-[11px] text-foreground-secondary">({item.actorRole})</span>
                <div className="flex items-center gap-1 text-[10px] font-semibold text-foreground-muted ml-1">
                  {getSourceIcon(item.systemSource)}
                  <span>{item.systemSource}</span>
                </div>
              </div>
              <p className="text-xs text-foreground/90 font-medium pl-0.5">
                {item.details}
              </p>
            </div>

            <div className="text-right whitespace-nowrap">
              <span className="text-[11px] font-semibold text-foreground-secondary block">
                {formatDate(item.timestamp)}
              </span>
              <span className="text-[10px] text-foreground-muted font-mono">
                {new Date(item.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
